import { create } from 'zustand';
import { SessionData } from 'src/services/sessionsService';

interface TutorSessionsState {
  // Data
  upcomingSessions: SessionData[];
  pastSessions: SessionData[];
  allSessions: SessionData[];
  
  // Loading states
  isLoadingUpcoming: boolean;
  isLoadingPast: boolean;
  isLoadingAll: boolean;
  
  // Error states
  upcomingError: string | null;
  pastError: string | null;
  allError: string | null;
  
  // UI State
  searchTerm: string;
  isCardView: boolean;
  selectedSession: SessionData | null;
  showReminderDialog: boolean;
  
  // Filter state
  filters: {
    status: string[];
    sessionType: string[];
    subject: string[];
  };
  
  // Column visibility state
  visibleColumns: {
    sessionId: boolean;
    student: boolean;
    subject: boolean;
    topic: boolean;
    subtopic: boolean;
    sessionType: boolean;
    dateTime: boolean;
    duration: boolean;
    status: boolean;
    actions: boolean;
  };
  
  // Actions
  setUpcomingSessions: (sessions: SessionData[]) => void;
  setPastSessions: (sessions: SessionData[]) => void;
  setAllSessions: (sessions: SessionData[]) => void;
  
  setLoadingUpcoming: (loading: boolean) => void;
  setLoadingPast: (loading: boolean) => void;
  setLoadingAll: (loading: boolean) => void;
  
  setUpcomingError: (error: string | null) => void;
  setPastError: (error: string | null) => void;
  setAllError: (error: string | null) => void;
  
  setSearchTerm: (term: string) => void;
  setIsCardView: (isCard: boolean) => void;
  setSelectedSession: (session: SessionData | null) => void;
  setShowReminderDialog: (show: boolean) => void;
  
  setFilters: (filters: Partial<TutorSessionsState['filters']>) => void;
  toggleFilter: (filterType: keyof TutorSessionsState['filters'], value: string) => void;
  clearAllFilters: () => void;
  
  setVisibleColumns: (columns: Partial<TutorSessionsState['visibleColumns']>) => void;
  toggleColumn: (column: keyof TutorSessionsState['visibleColumns']) => void;
  
  // Computed getters
  getFilteredSessions: () => SessionData[];
  getFilterOptions: () => {
    statuses: string[];
    sessionTypes: string[];
    subjects: string[];
  };
  hasActiveFilters: () => boolean;
}

export const useTutorSessionsStore = create<TutorSessionsState>((set, get) => ({
  // Initial data state
  upcomingSessions: [],
  pastSessions: [],
  allSessions: [],
  
  // Initial loading states
  isLoadingUpcoming: false,
  isLoadingPast: false,
  isLoadingAll: false,
  
  // Initial error states
  upcomingError: null,
  pastError: null,
  allError: null,
  
  // Initial UI state
  searchTerm: '',
  isCardView: false,
  selectedSession: null,
  showReminderDialog: false,
  
  // Initial filter state
  filters: {
    status: [],
    sessionType: [],
    subject: [],
  },
  
  // Initial column visibility state
  visibleColumns: {
    sessionId: true,
    student: true,
    subject: true,
    topic: true,
    subtopic: false,
    sessionType: true,
    dateTime: true,
    duration: true,
    status: true,
    actions: true,
  },
  
  // Data setters
  setUpcomingSessions: (sessions) => set({ upcomingSessions: sessions }),
  setPastSessions: (sessions) => set({ pastSessions: sessions }),
  setAllSessions: (sessions) => set({ allSessions: sessions }),
  
  // Loading setters
  setLoadingUpcoming: (loading) => set({ isLoadingUpcoming: loading }),
  setLoadingPast: (loading) => set({ isLoadingPast: loading }),
  setLoadingAll: (loading) => set({ isLoadingAll: loading }),
  
  // Error setters
  setUpcomingError: (error) => set({ upcomingError: error }),
  setPastError: (error) => set({ pastError: error }),
  setAllError: (error) => set({ allError: error }),
  
  // UI setters
  setSearchTerm: (term) => set({ searchTerm: term }),
  setIsCardView: (isCard) => set({ isCardView: isCard }),
  setSelectedSession: (session) => set({ selectedSession: session }),
  setShowReminderDialog: (show) => set({ showReminderDialog: show }),
  
  // Filter actions
  setFilters: (newFilters) => 
    set((state) => ({ 
      filters: { ...state.filters, ...newFilters } 
    })),
  
  toggleFilter: (filterType, value) =>
    set((state) => ({
      filters: {
        ...state.filters,
        [filterType]: state.filters[filterType].includes(value)
          ? state.filters[filterType].filter(item => item !== value)
          : [...state.filters[filterType], value]
      }
    })),
  
  clearAllFilters: () =>
    set({
      filters: {
        status: [],
        sessionType: [],
        subject: [],
      }
    }),
  
  // Column visibility actions
  setVisibleColumns: (newColumns) =>
    set((state) => ({
      visibleColumns: { ...state.visibleColumns, ...newColumns }
    })),
  
  toggleColumn: (column) =>
    set((state) => ({
      visibleColumns: {
        ...state.visibleColumns,
        [column]: !state.visibleColumns[column]
      }
    })),
  
  // Computed getters
  getFilteredSessions: () => {
    const state = get();
    const { upcomingSessions, searchTerm, filters } = state;
    
    return upcomingSessions.filter(session => {
      // Search term filter
      if (searchTerm) {
        const studentName = `${session.student_profile?.first_name || ''} ${session.student_profile?.last_name || ''}`.toLowerCase();
        const topicName = session.topic?.name?.toLowerCase() || '';
        const batchName = session.batch?.name?.toLowerCase() || '';
        const sessionId = session.id.toLowerCase();

        const matchesSearch = studentName.includes(searchTerm.toLowerCase()) ||
                             topicName.includes(searchTerm.toLowerCase()) ||
                             batchName.includes(searchTerm.toLowerCase()) ||
                             sessionId.includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters.status.length > 0 && !filters.status.includes(session.status)) {
        return false;
      }

      // Session type filter
      if (filters.sessionType.length > 0 && !filters.sessionType.includes(session.session_type)) {
        return false;
      }

      // Subject filter
      if (filters.subject.length > 0) {
        const sessionSubject = session.topic?.subject?.name || session.topic?.name || '';
        if (!filters.subject.includes(sessionSubject)) {
          return false;
        }
      }

      return true;
    });
  },
  
  getFilterOptions: () => {
    const { upcomingSessions } = get();
    
    const statuses = [...new Set(upcomingSessions.map(s => s.status))];
    const sessionTypes = [...new Set(upcomingSessions.map(s => s.session_type))];
    const subjects = [...new Set(upcomingSessions.map(s => s.topic?.subject?.name || s.topic?.name).filter(Boolean))];

    return { statuses, sessionTypes, subjects };
  },
  
  hasActiveFilters: () => {
    const { filters } = get();
    return filters.status.length > 0 || filters.sessionType.length > 0 || filters.subject.length > 0;
  },
}));